import React from 'react';
import { cartLabels } from '@/data/content';
import Button from '@/components/ui/Button';
import { SpinnerIcon } from '@/components/ui/Icons';

interface AddToBasketFullProps {
    quantity: number;
    minQuantity: number;
    maxQuantity: number;
    disabled?: boolean;
    validating?: boolean;
    hasDate?: boolean;
    onDecrement: (e: React.MouseEvent) => void;
    onIncrement: (e: React.MouseEvent) => void;
    onAdd: (e: React.MouseEvent) => void;
}

const AddToBasketFull: React.FC<AddToBasketFullProps> = ({
    quantity,
    minQuantity,
    maxQuantity,
    disabled,
    validating,
    hasDate = true,
    onDecrement,
    onIncrement,
    onAdd,
}) => {
    const isAtMin = quantity <= minQuantity;
    const isAtMax = quantity >= maxQuantity;

    return (
        <div className="flex items-center justify-end px-4 sm:px-5 pb-4 sm:pb-5 pt-3 border-t border-app-border">
            <div className="flex items-center gap-2.5 w-full sm:w-auto justify-between sm:justify-end">
                {hasDate && (
                    <div className="flex items-center bg-app-bg border border-app-border rounded-lg p-0.5">
                        <Button
                            variant="ghost"
                            type="button"
                            disabled={isAtMin}
                            onClick={onDecrement}
                            className="inline-flex items-center justify-center font-bold rounded-2xl transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-app-primary px-6 py-3 text-base !p-0 w-8 h-8 rounded-md"
                            aria-label="Moins"
                        >
                            <span className="text-lg leading-none select-none">−</span>
                        </Button>
                        <span
                            className="w-6 text-center text-sm font-bold text-app-text select-none"
                            data-testid="quantity"
                        >
                            {quantity}
                        </span>
                        <Button
                            variant="ghost"
                            type="button"
                            disabled={isAtMax}
                            onClick={onIncrement}
                            className="inline-flex items-center justify-center font-bold rounded-2xl transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-app-primary px-6 py-3 text-base !p-0 w-8 h-8 rounded-md"
                            aria-label="Plus"
                        >
                            <span className="text-lg leading-none select-none">+</span>
                        </Button>
                    </div>
                )}

                <Button
                    variant="primary"
                    type="button"
                    disabled={disabled || validating}
                    onClick={onAdd}
                    className="inline-flex items-center justify-center font-bold rounded-2xl transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed bg-app-primary text-white shadow-lg shadow-app-primary/20 hover:bg-orange-600 hover:shadow-xl hover:shadow-app-primary/30 hover:-translate-y-0.5 px-6 py-3 text-base h-9 !px-4 !py-0 rounded-xl gap-2 text-xs font-black uppercase tracking-tight"
                    aria-label={validating ? cartLabels.adding_label : cartLabels.add_button}
                    title={validating ? cartLabels.adding_label : cartLabels.add_button}
                >
                    {validating ? (
                        <span className="flex items-center gap-2">
                            <SpinnerIcon />
                            <span>{cartLabels.adding_label}</span>
                        </span>
                    ) : (
                        <>
                            <span className="hidden xl:inline">{cartLabels.add_button}</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                            </svg>
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};

export default AddToBasketFull;
